import { Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Switch, useDisclosure } from "@nextui-org/react";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import * as yup from "yup";
import toast from "react-hot-toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { patchFetcher } from "../../../../../services/api";
import { getErrorMessage } from "../../../../../services/errorMessage";
import ControlledInput from "../../../../elements/ControlledInput";
import { month } from "../../../../../enums/month";

/**
 * Schema didefinisikan di luar komponen supaya tidak dibuat ulang setiap render.
 *
 * peruntukanPenggunaan ikut divalidasi di sini - sebelumnya field itu dikirim ke
 * API tanpa pernah melewati schema sama sekali.
 */
const schema = yup.object().shape({
  laporanTersedia: yup.boolean().required(),

  jumlahB3Digunakan: yup
    .number()
    // Input type="number" yang dikosongkan menghasilkan "", dan yup mengubahnya
    // menjadi NaN. Tanpa transform ini, NaN diserialisasi jadi null oleh
    // JSON.stringify dan server menolaknya dengan 400.
    .transform((value, originalValue) => (originalValue === "" || originalValue === null ? undefined : value))
    .typeError("Jumlah harus berupa angka")
    .when("laporanTersedia", {
      is: true,
      then: (s) => s.required("Harus diisi").min(0, "Tidak boleh negatif"),
      otherwise: (s) => s.notRequired(),
    }),

  peruntukanPenggunaan: yup.string().when("laporanTersedia", {
    is: true,
    then: (s) => s.trim().required("Harus diisi"),
    otherwise: (s) => s.notRequired(),
  }),
});

export default function ModalEditReport({ data, mutate }) {
  const [isExistReport, setIsExistReport] = useState(true);
  const {
    register,
    control,
    setValue,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm({ resolver: yupResolver(schema) });
  const { isOpen, onOpenChange, onClose } = useDisclosure();

  function handleOnClose() {
    // PENTING: setIsExistReport dipanggil terpisah, tidak di dalam objek reset.
    //
    // Versi sebelumnya menulis `laporanTersedia: setIsExistReport(true)`.
    // setIsExistReport mengembalikan undefined, jadi field laporanTersedia di
    // form selalu undefined - bukan boolean. Akibatnya seluruh validasi
    // bersyarat yup.when("laporanTersedia") tidak pernah aktif, dan nilai tidak
    // valid lolos ke server.
    setIsExistReport(true);
    reset({
      laporanTersedia: true,
      jumlahB3Digunakan: "",
      peruntukanPenggunaan: "",
    });
    onClose();
  }

  function existReport(val) {
    setIsExistReport(val);
    // Jaga agar nilai di form selalu sinkron dengan switch, tidak hanya
    // mengandalkan onChange bawaan register.
    setValue("laporanTersedia", val);

    if (!val) {
      // Laporan tidak tersedia: kosongkan kedua field agar tidak ada sisa nilai
      // dari input yang sudah disembunyikan.
      setValue("jumlahB3Digunakan", 0);
      setValue("peruntukanPenggunaan", "");
    }
  }

  function handleOnOpen() {
    const laporanTersedia = data?.laporanTersedia ?? true;

    setIsExistReport(laporanTersedia);
    reset({
      laporanTersedia,
      // Nilai null dari database diubah jadi "" supaya input terkendali dan
      // transform di schema bisa menanganinya.
      jumlahB3Digunakan: data?.jumlahB3Digunakan ?? "",
      peruntukanPenggunaan: data?.peruntukanPenggunaan ?? "",
    });
    onOpenChange();
  }

  async function handleOnSubmit(formData) {
    // Payload dinormalkan secara eksplisit, tidak dikirim mentah dari form.
    const payload = {
      laporanTersedia: formData.laporanTersedia,
      jumlahB3Digunakan: formData.laporanTersedia ? Number(formData.jumlahB3Digunakan) : 0,
      peruntukanPenggunaan: formData.laporanTersedia ? (formData.peruntukanPenggunaan ?? "").trim() : "",
    };

    try {
      await patchFetcher(`/api/pelaporan-penggunaan-bahan-b3/edit`, data?.id, payload);
      await mutate();
      toast.success("Laporan bahan b3 berhasil diubah!");
      handleOnClose();
    } catch (error) {
      // getErrorMessage menjamin hasilnya string. Versi sebelumnya meneruskan
      // error?.response?.data?.message apa adanya - dan ketika backend membalas
      // message berbentuk objek per-field, objek itu masuk ke toast dan React
      // melempar error #31 "Objects are not valid as a React child", yang
      // membuat seluruh halaman putih.
      toast.error(getErrorMessage(error, "Gagal ubah laporan bahan b3!"));
    }
  }

  return (
    <>
      <Button size="sm" onPress={handleOnOpen} color="warning" isIconOnly>
        <PencilSquareIcon className="size-4" />
      </Button>
      <Modal size="xl" isOpen={isOpen} onOpenChange={onOpenChange} onClose={handleOnClose} isDismissable={false} isKeyboardDismissDisabled={false}>
        <ModalContent>
          {(onClose) => (
            <form onSubmit={handleSubmit(handleOnSubmit)}>
              <ModalHeader className="flex flex-col gap-1">Ubah Penggunaan Jenis B3</ModalHeader>
              <ModalBody className="flex flex-col gap-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input isRequired isDisabled label="Perusahaan" value={data?.company?.name} labelPlacement="outside" variant="faded" />
                  <Input isRequired isDisabled label="Periode" value={data?.period?.name} labelPlacement="outside" variant="faded" />
                  <Input isRequired isDisabled label="Bulan" value={month[data?.bulan - 1]} labelPlacement="outside" variant="faded" />
                  <Input isRequired isDisabled label="Tahun" value={data?.tahun} labelPlacement="outside" variant="faded" />
                  <Input isRequired isDisabled label="Jenis B3" value={`${data?.dataBahanB3?.casNumber} - ${data?.dataBahanB3?.namaBahanKimia}`} labelPlacement="outside" variant="faded" />
                </div>
                <Switch
                  {...register("laporanTersedia", {
                    onChange: (e) => {
                      existReport(e.target.checked);
                    },
                  })}
                  size="sm"
                  isSelected={isExistReport}
                >
                  {isExistReport ? "Laporan Tersedia" : "Tidak ada laporan penggunaan"}
                </Switch>
                {isExistReport && (
                  <>
                    <ControlledInput control={control} label="Jumlah Digunakan (KG)" name="jumlahB3Digunakan" type="number" isRequired={true} />
                    <ControlledInput control={control} label="Peruntukan Penggunaan" name="peruntukanPenggunaan" type="text" isRequired={true} />
                  </>
                )}
              </ModalBody>
              <ModalFooter>
                <Button isDisabled={isSubmitting} color="danger" variant="light" onPress={onClose}>
                  Tutup
                </Button>
                <Button isLoading={isSubmitting} isDisabled={isSubmitting} type="submit" color="primary">
                  Simpan
                </Button>
              </ModalFooter>
            </form>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}